import { ViewportChangedEvent } from '@ag-grid-community/core';
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule } from '@angular/forms';
import moment from 'moment';
import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { MatDividerModule } from '@angular/material/divider';
import { Subscription } from 'rxjs';

import { UNDERSCORE } from '../../../../shared/utils/constant';
import { formatNumber } from '../../../../shared/utils/number.format';
import { FormNeed4SteelOffer } from '../../../models/form-mill-offer';
import {
	BocClient,
	DATETIME_FORMAT_PATTERN,
	MillOfferNeed4Steel,
	RequestOrderItem,
	RequestProductData,
	VARIABLE_SPECIFIC_RULE, isVariableSpecificField, getValidPayer,
	getTmodeTrKey,
	DEFAULT_TRANSPORT_MODE
} from '../../../models/need4steel';
import { Need4SteelWizardStep } from '../../../models/need4steel-wizard-state';
import { MillOfferService } from '../../../services/mill-offer.service';
import { Need4SteelWizardStepperService } from '../../../services/need4steel-wizard-stepper.service';
import { Need4SteelWizardService } from '../../../services/need4steel-wizard.service';
import { WizardPageContent } from '../wizard-page-content';
import { NAMESPACE_A2C_FIELD, ProductCharacteristicsTranslationService }
	from '../../../../shared/services/product-characteristics-translation.services';
import { NumberPipe } from '../../../../shared/pipes/number.pipe';

interface OrderLine {
	price: string;
	maxCoilWeight: number;
	coil: number;
	steelGrade: string;
	thickness: number;
	width: number;
	qliId: string;
	sqnNumber: string;
	maxSpecificWeight?: number;
}

@Component({
	selector: 'copo-need4steel-wizard-stepsummary',
	templateUrl: './step-summary.component.html',
	styleUrl: './step-summary.component.css',
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		MatTooltipModule,
		NgClass,
		NgIf,
		MatDividerModule,
		FormsModule,
		NgFor,
		DecimalPipe,
		NumberPipe
	]
})
export class StepSummaryComponent extends WizardPageContent implements OnInit, OnDestroy {
	form: FormNeed4SteelOffer;
	largeScreen = true;
	millOffer?: MillOfferNeed4Steel;
	coNbr: string;
	soldtoShiptoList?: BocClient[];
	enduser?: string;
	showEndUser = false;
	shipto?: string;
	maxSpecCoilWeightOptionAvailable = false;
	soldto?: string;
	productFormGroup!: FormGroup;
	isShipmentSelectionEnabled = false;
	readonly UNDERSCORE = UNDERSCORE;
	readonly VARIABLE_SPECIFIC_RULE = VARIABLE_SPECIFIC_RULE;
	readonly isVariableSpecificField = isVariableSpecificField;
	private exchangeRateSubscription?: Subscription;
	private readonly VIEW_STEP_NUM = 1;

	constructor(
		private readonly need4steelWizardService: Need4SteelWizardService,
		private readonly need4steelStepperService: Need4SteelWizardStepperService,
		private readonly millOfferService: MillOfferService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly changeDetectorRef: ChangeDetectorRef,
	) {
		super(Need4SteelWizardStep.SUMMARY, need4steelWizardService.getState());
		this.form = this.need4steelWizardService.getStateOfferForm();
		this.coNbr = this.form.getFormCoNbr().value ? <string> this.form.getFormCoNbr().value : UNDERSCORE;
		const pdtFormGroup = this.millOfferService.productFormGroup;
		this.millOffer = this.millOfferService.millOffer;
		this.maxSpecCoilWeightOptionAvailable = this.millOfferService.maxSpecCoilWeightOptionAvailable;
		if (pdtFormGroup) {
			this.createSubmitReqObj(pdtFormGroup);
		}
	}

	get productResultControl() {
		return (<FormArray> this.productFormGroup.controls['productSpecifications']).controls;
	}

	get productFormArray() {
		return <FormArray> this.productFormGroup.get('productSpecifications');
	}

	@HostListener('window:resize') onResize() {
		this.largeScreen = innerWidth > 1086;
	}

	ngOnInit() {
		this.largeScreen = innerWidth > 1086;
		this.isShipmentSelectionEnabled = this.need4steelWizardService.isShipmentSelectionEnabled(
			this.millOffer?.millSite
		);

		if (this.millOfferService.productFormGroup) {
			this.productFormGroup = this.millOfferService.productFormGroup;
			for (let i = 0; i < this.productFormArray.length; i++) {
				const control = <FormArray> this.productFormGroup.controls['productSpecifications'];
				control.controls[i].get('variableFormGroup')?.enable();
			}
		}
		this.soldtoShiptoList = this.millOfferService.filteredSoldtoShiptoList;
		const selSoldtoId = <string> this.form.getFormSoldTo().value;
		const selectedShiptoId = <string> this.form.getFormShipTo().value;
		if (this.soldtoShiptoList) {
			const selectedSoldto = this.millOfferService.bocs?.get(selSoldtoId);
			if (selectedSoldto) {
				this.soldto = `${ selectedSoldto.name } - ${ selectedSoldto.city }`;
			}
			const selectedShiptoList = this.soldtoShiptoList.filter(v => v.businessId === selectedShiptoId);
			for (const shiptoList of selectedShiptoList) {
				this.shipto = `${ shiptoList.name } - ${ shiptoList.city }`;
			}
		}
		const selectedSoldtoGroup = this.need4steelWizardService.userBocData?.find(
			group => group.soldTo === selSoldtoId,
		);
		this.showEndUser = (selectedSoldtoGroup?.endUsers.length ?? 0) > 1;
		if (this.showEndUser) {
			const endUserId = <string> this.form.getFormEndUser().value;
			const endUserDetails = this.millOfferService.bocs?.get(endUserId);
			this.enduser = endUserDetails
				? `${ endUserDetails.name } - ${ endUserDetails.city }`
				: endUserId;
		}
		this.loadExchangeRateIfNeeded();
		this.need4steelStepperService.onStepChanged.emit({ step: this.VIEW_STEP_NUM });
	}

	checkError() {
		return false;
	}

	getSummaryEmptyColumns(): undefined[] {
		const topRowColumnCount = 3 + (this.enduser ? 1 : 0) + (this.isShipmentSelectionEnabled ? 1 : 0);
		const bottomRowColumnCount = 2 + (this.hasExchangeRateToDisplay() ? 2 : 0);
		return Array.from(
			{ length: Math.max(0, topRowColumnCount - bottomRowColumnCount) },
			(): undefined => undefined,
		);
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	createSubmitReqObj(pdtGroup: FormGroup) {
		const requestOrderItemList: RequestOrderItem[] = [];
		const controlArray = <FormArray> pdtGroup.get('productSpecifications');
		if (this.millOffer) {
			const soldTo = String(this.form.getFormSoldTo().value).padStart(10, '0');
			const shipTo = String(this.form.getFormShipTo().value).padStart(10, '0');
			const payer = getValidPayer(this.form.getFormPayer().value);
			const endUser = String(this.form.getFormEndUser().value).padStart(10, '0');
			const delvryWeek = this.millOffer.deliveryWeek;
			const delvryYear = this.millOffer.deliveryYear;
			const displayDeliveryDate = `W${ delvryWeek < 10 ? '0' : '' }${ delvryWeek }/${ delvryYear }`;
			const deliveryDate = this.getDateByWeek(delvryWeek, delvryYear);
			const formatedDeliveryDate = deliveryDate.format(DATETIME_FORMAT_PATTERN);
			const selectedWeightType = String(this.form.getFormWeightType().value);
			let totalOrderAmount = 0;
			for (const [index, control] of controlArray.controls.entries()) {
				const pdt = <OrderLine> control.value;
				if (pdt.price !== this.UNDERSCORE) {
					const variableGroup = <object> control.get('variableFormGroup')?.value;
					const totalWeight = pdt.maxCoilWeight * pdt.coil;
					totalOrderAmount += totalWeight * Number(pdt.price);
					const steelGradeCode = this.getFieldCode('steelGrade', pdt.steelGrade);
					const requestProductDataList: RequestProductData[] = [
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'steelGrade',
							characValueText: String(steelGradeCode),
							characValueUnitTr: '',
							characValueTable: '',
							characValueNum: undefined,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: this.getFieldType('steelGrade')
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'thicknessValue',
							characValueText: '',
							characValueUnitTr: 'mm',
							characValueTable: '',
							characValueNum: pdt.thickness,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: this.getFieldType('thicknessValue')
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'widthValue',
							characValueText: '',
							characValueUnitTr: 'mm',
							characValueTable: '',
							characValueNum: pdt.width,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: this.getFieldType('widthValue')
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'maxNetWeightValue',
							characValueText: '',
							characValueUnitTr: 't',
							characValueTable: '',
							characValueNum: pdt.maxCoilWeight,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: this.getFieldType('maxNetWeightValue')
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'category',
							characValueText: this.millOffer.category,
							characValueUnitTr: '',
							characValueTable: '',
							characValueNum: undefined,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: 'string'
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'subCategory',
							characValueText: this.millOffer.subCategory,
							characValueUnitTr: '',
							characValueTable: '',
							characValueNum: undefined,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: 'string'
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'form',
							characValueText: this.millOffer.form,
							characValueUnitTr: '',
							characValueTable: '',
							characValueNum: undefined,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: 'string'
						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'finishingLine',
							characValueText: this.millOffer.millSiteCode,
							characValueUnitTr: '',
							characValueTable: '',
							characValueNum: undefined,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: this.getFieldType('finishingLine')

						},
						{
							id: undefined,
							requestOrderItemId: undefined,
							characName: 'maxWeightPerWidthUnitValue',
							characValueText: '',
							characValueUnitTr: 'kg/mm',
							characValueTable: '',
							characValueNum: pdt.maxSpecificWeight,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: '',
							type: this.getFieldType('maxWeightPerWidthUnitValue')
						}
					];

					const varFieldKeys = Object.keys(variableGroup);
					const variableMap = new Map(Object.entries(variableGroup));
					for (const field of varFieldKeys) {
						if (isVariableSpecificField(field)) {
							continue;
						}
						const variableValue = <string> variableMap.get(field);
						const variableCode = this.getFieldCode(field, variableValue, pdt.qliId);
						const fieldName = field;
						const fieldType = this.getFieldType(fieldName);
						const requestProductData: RequestProductData = {
							id: undefined,
							requestOrderItemId: undefined,
							characName: fieldName,
							type: fieldType,
							characValueText: variableCode,
							characValueUnitTr: '',
							characValueTable: '',
							characValueNum: undefined,
							creationDate: undefined,
							creationBy: '',
							modificationDate: undefined,
							modificationBy: ''
						};
						requestProductDataList.push(requestProductData);
					}
					const requestOrderItem: RequestOrderItem = {
						id: undefined,
						requestOrderId: undefined,
						itemNumber: (index + 1).toString().padStart(3, '0'),
						productRef: '',
						qliId: pdt.qliId,
						connectionKey: '',
						configurationRef: '',
						templateRef: String(this.millOffer.templateNumber),
						productStatusTr: '',
						soldToRef: soldTo,
						shipToRef: shipTo,
						transportModeTr: String(this.form.getFormTmode().value) || DEFAULT_TRANSPORT_MODE,
						deliveryDateTypeTr: '',
						deliveryDate: formatedDeliveryDate,
						deliveryDateDisplay: displayDeliveryDate,
						incotermTr: String(this.form.getFormIncoterm().value),
						incoterm2Tr: '',
						quantityAmount: pdt.coil,
						quantityUnitTr: 'pc',
						priceAmount: undefined,
						priceCurrencyTr: 'EUR',
						priceWeightTr: 't',
						creationDate: undefined,
						creationBy: '',
						modificationDate: undefined,
						modificationBy: '',
						endUserRef: endUser,
						totalAmount: totalWeight,
						totalUnitTr: 't',
						listOfRequestProductData: requestProductDataList,
						listOfRequestServiceLevel: [
							{
								id: undefined,
								requestOrderItemId: undefined,
								serviceLevelRef: '',
								creationDate: undefined,
								creationBy: ''
							}
						],
						sqnNumber: pdt.sqnNumber
					};
					requestOrderItemList.push(requestOrderItem);
				}
			}
			this.millOfferService.payer = payer;
			this.millOfferService.totalOrderAmount = totalOrderAmount;
			this.millOfferService.requestOrderObj = {
				id: undefined,
				creatorUserRef: undefined,
				organizationRef: undefined,
				transportModeTr: Number(this.form.getFormTmode().value) || Number(DEFAULT_TRANSPORT_MODE),
				soldToRef: soldTo,
				shipToRef: shipTo,
				endUserRef: endUser,
				rfqNumber: this.millOffer.rfqNumber || '',
				offerRef: this.millOffer.id,
				purchaseOrderNumber: String(this.form.getFormCoNbr().value),
				deliveryDateTypeTr: '',
				deliveryDateDisplay: displayDeliveryDate,
				iddTr: this.millOffer.idd,
				orderStatusTr: '',
				deliveryDate: formatedDeliveryDate,
				creationDate: undefined,
				creationBy: '',
				modificationDate: undefined,
				modificationBy: '',
				coilWeightType: selectedWeightType,
				dealType: this.millOffer.dealType,
				codeUnitRef: this.millOfferService.boc.referenceCommercialUnit?.codeUnit,
				codeSubUnitRef: this.millOfferService.boc.referenceCommercialSubUnit?.codeSubUnit,
				codeSubSubUnitRef: this.millOfferService.boc.referenceCommercialSubSubUnit?.codeSubSubUnit,
				exchangeRate: this.getExchangeRateNumber(),
				exchangeRateReferenceDate: this.getExchangeRateDisplayReferenceDate() || undefined,
				listOfRequestOrderItem: requestOrderItemList,
				millSite: this.millOffer.millSite,
				totalAmount: totalOrderAmount
			};
			if (payer !== undefined) {
				this.millOfferService.requestOrderObj.payer = payer;
			}
		}
	}

	ngOnDestroy() {
		this.exchangeRateSubscription?.unsubscribe();
	}

	getDateByWeek(deliveryWeek: number, deliveryYear: number) {
		return moment(`${ deliveryYear }-W${ deliveryWeek }`, 'GGGG-[W]WW').hour(12).isoWeekday(1);
	}

	getFieldCode(fieldName: string, fieldValue: string, qliId?: string) {
		if (isVariableSpecificField(fieldName)) {
			return fieldValue;
		}
		if (this.millOffer?.dplconstraintsList) {
			const variableOptions = this.millOffer.dplconstraintsList
				.filter(scope => !qliId || scope.qliId === qliId)
				.flatMap(item => item.settingGroups)
				.flatMap(v => v.settings)
				.filter(v => v.fieldName.toLowerCase() === fieldName.toLowerCase())
				.flatMap(v => v.attributeValues)
				.filter(v => v.description.toLowerCase() === fieldValue.toLowerCase())
				.map(v => v.code)
				.filter((value, index, self) => self.indexOf(value) === index)
				.sort((a, b) => a.localeCompare(b));
			return variableOptions.length === 1 ? variableOptions[0] : '';
		}
		return '';
	}

	getControl(formIndex: number, controlName: string) {
		const controlArray = <FormArray> this.productFormGroup.get('productSpecifications');
		return <FormControl>controlArray.controls[formIndex].get(controlName);
	}

	getWeightType() {
		return <string> this.form.getFormWeightType().value;
	}

	getTotalWeightDecimal(formIndex: number) {
		const controlArray = <FormArray> this.productFormGroup.get('productSpecifications');
		const maxCoilWeightControl = <FormControl> controlArray.controls[formIndex].get('maxCoilWeight');
		const coilControl = <FormControl> controlArray.controls[formIndex].get('coil');
		return maxCoilWeightControl.value && coilControl.value && maxCoilWeightControl.valid
			&& coilControl.valid ? maxCoilWeightControl.value * coilControl.value : 0;
	}

	getVariableListValue(index: number, field: string) {
		const controlArray = <FormArray> this.productFormGroup.get('productSpecifications');
		const variableFormControl = <FormControl>controlArray.controls[index].get('variableFormGroup')?.get(field);
		return variableFormControl.value ? String(variableFormControl.value) : '';
	}

	getFieldType(fieldName: string) {
		if (this.millOffer?.variablesList) {
			const variableLists = this.millOffer.variablesList;
			for (const field of variableLists) {
				if (fieldName === field.field) {
					return field.templateProperty.type;
				}
			}
		}
		return 'string';
	}

	getVariableFieldLength(index: number) {
		const controlArray = <FormArray> this.productFormGroup.get('productSpecifications');
		const variableFormGroup = <FormGroup>controlArray.controls[index].get('variableFormGroup');
		return Object.keys(variableFormGroup.controls).map(field => ({
			field,
			description: this.getFieldLabel(field)
		}));
	}

	getTotalPrice(formIndex: number) {
		const controlArray = <FormArray> this.productFormGroup.get('productSpecifications');
		const effectivePrice = <string>(<FormControl>controlArray.controls[formIndex].get('price')).value;
		if (Number.isNaN(Number(effectivePrice))) {
			return effectivePrice;
		}
		return formatNumber(<number><unknown>effectivePrice, 'amount');
	}

	getSoldToBocListSize(soldToBocList: BocClient[]) {
		return soldToBocList.length;
	}

	getFieldLabel(fieldLabel: string) {
		return this.productCharacteristicsTranslationService
			.translate(`${ NAMESPACE_A2C_FIELD }${ fieldLabel }`);
	}

	hasExchangeRateToDisplay(): boolean {
		return this.need4steelWizardService.hasExchangeRateToDisplay();
	}

	getTransportModeTrKey(): string {
		return getTmodeTrKey(<string> this.form.getFormTmode().value);
	}

	shouldHighlightExchangeRate(): boolean {
		return this.need4steelWizardService.shouldHighlightExchangeRate();
	}

	getExchangeRateDisplayReferenceDate() {
		return this.need4steelWizardService.getExchangeRateReferenceDate() || '';
	}

	getExchangeRateDisplayValue(): string | undefined {
		return this.need4steelWizardService.getExchangeRateDisplayValue();
	}

	getExchangeRateDisplayCurrency(): string | undefined {
		return this.need4steelWizardService.getExchangeRateDisplayCurrency();
	}

	getPriceNumber(formIndex: number): number {
		const controlArray = <FormArray> this.productFormGroup.get('productSpecifications');
		const effectivePrice = <string>(<FormControl>controlArray.controls[formIndex].get('price')).value;
		return Number(effectivePrice);
	}

	getEstimatedPrice(formIndex: number): string {
		const currency = this.need4steelWizardService.getInvoicingCurrency();
		if (!currency) {
			return '';
		}
		const exchangeRate = this.need4steelWizardService.getExchangeRate(currency);
		if (!exchangeRate) {
			return '';
		}
		const price = this.getPriceNumber(formIndex);
		if (!Number.isFinite(price)) {
			return '';
		}
		return `${ formatNumber(Math.round(price * exchangeRate.value * 100) / 100, 'amount') } ${ currency }`;
	}

	private getExchangeRateNumber() {
		const currency = this.need4steelWizardService.getInvoicingCurrency();
		if (!currency) {
			return undefined;
		}
		return this.need4steelWizardService.getExchangeRate(currency)?.value;
	}

	private loadExchangeRateIfNeeded() {
		if (!this.need4steelWizardService.shouldDisplayExchangeRate()) {
			return;
		}
		this.exchangeRateSubscription = this.need4steelWizardService.loadExchangeRates().subscribe({
			next: () => {
				this.changeDetectorRef.markForCheck();
			},
			error: (err: unknown) => {
				console.error('error loading exchange rate', err);
				this.changeDetectorRef.markForCheck();
			}
		});
	}
}
